import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { OtherSideTransactionService } from '../../platform/services/other-side-transaction.service';
import {
  getOtherSideTransactionLimitsAction,
  getOtherSideTransactionLimitsErrorAction,
  getOtherSideTransactionLimitsSuccessAction,
} from '../actions/other-side-transaction-limits.actions';
import {
  create2FAOtherSideTransactionAction,
  createOtherSideTransactionAction,
  createOtherSideTransactionErrorAction,
  createOtherSideTransactionSuccessAction,
  getOtherSideTransactionAction,
  getOtherSideTransactionListAction,
  getOtherSideTransactionsErrorAction,
  getOtherSideTransactionsListErrorAction,
  getOtherSideTransactionsListSuccessAction,
  getOtherSideTransactionsSuccessAction,
  payoutExportAction,
  payoutExportErrorAction,
  payoutExportSuccessAction,
  retryWithdrawAction,
  retryWithdrawErrorAction,
  retryWithdrawSuccessAction,
  updatePayoutAction,
  updatePayoutErrorAction,
  updatePayoutStatusAction,
  updatePayoutStatusErrorAction,
  updatePayoutStatusSuccessAction,
  updatePayoutSuccessAction,
} from '../actions/other-side-transaction.actions';
import { MessageService } from '../services/message.service';

@Injectable()
export class OtherSideTransactionEffects {
  private readonly actions$ = inject(Actions);
  private readonly otherSideTransactionService = inject(OtherSideTransactionService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);

  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getOtherSideTransactionAction),
      switchMap(({ transactionId, isAdmin }) =>
        this.otherSideTransactionService.get(transactionId, isAdmin).pipe(
          map((transaction) => getOtherSideTransactionsSuccessAction({ transaction })),
          catchError(({ errors }) => {
            this.messageService.showErrors(errors, 'Failed to get payout');
            return of(getOtherSideTransactionsErrorAction({ errors }));
          }),
        ),
      ),
    ),
  );

  withdrawRetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retryWithdrawAction),
      switchMap(({ transactionId }) =>
        this.otherSideTransactionService.withdrawRetry(transactionId).pipe(
          map((transaction) => retryWithdrawSuccessAction({ transaction })),
          catchError((errors) => of(retryWithdrawErrorAction(errors))),
        ),
      ),
    ),
  );

  getHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getOtherSideTransactionListAction),
      mergeMap(({ isAdmin, filters, paging }) =>
        this.otherSideTransactionService.getOsTransactionHistory(isAdmin, paging, filters).pipe(
          map((transactions) => getOtherSideTransactionsListSuccessAction({ transactions })),
          catchError((errors) => of(getOtherSideTransactionsListErrorAction(errors))),
        ),
      ),
    ),
  );

  payoutExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(payoutExportAction),
      switchMap(({ isAdmin, filters, paging, uuid: uuid }) =>
        this.otherSideTransactionService.postPayoutHistoryExport(isAdmin, paging, filters, uuid).pipe(
          map((transactions) => payoutExportSuccessAction()),
          catchError((errors) => of(payoutExportErrorAction(errors))),
        ),
      ),
    ),
  );

  createOsTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOtherSideTransactionAction),
      switchMap(({ otherSideTransaction }) =>
        this.otherSideTransactionService.create(otherSideTransaction).pipe(
          map((otherSideTransaction) => createOtherSideTransactionSuccessAction({ otherSideTransaction })),
          catchError((errors) => of(createOtherSideTransactionErrorAction(errors))),
        ),
      ),
    ),
  );

  createOsTransaction2FA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(create2FAOtherSideTransactionAction),
      switchMap(({ otherSideTransaction, twoFa }) =>
        this.otherSideTransactionService.create2FA(otherSideTransaction, twoFa).pipe(
          map((otherSideTransaction) => createOtherSideTransactionSuccessAction({ otherSideTransaction })),
          catchError((errors) => of(createOtherSideTransactionErrorAction(errors))),
        ),
      ),
    ),
  );

  changePayoutStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePayoutStatusAction),
      switchMap(({ hashId, action, twoFa }) =>
        this.otherSideTransactionService.changePayoutStatus(hashId, action, twoFa).pipe(
          map((transaction) => updatePayoutStatusSuccessAction({ transaction, action })),
          catchError(({ errors }) => of(updatePayoutStatusErrorAction({ errors, action }))),
        ),
      ),
    ),
  );

  showSuccessMessageOnPayoutStatusChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updatePayoutStatusSuccessAction),
        tap(() => {
          this.messageService.success(`Payout status changed!`);
        }),
      ),
    { dispatch: false },
  );

  showErrorMessageOnPayoutStatusChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updatePayoutStatusErrorAction),
        tap(({ errors, action }) => {
          this.messageService.showErrors(errors, `Failed to ${action} payout`);
        }),
      ),
    { dispatch: false },
  );

  getTransactionLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getOtherSideTransactionLimitsAction),
      mergeMap(() =>
        this.otherSideTransactionService.getLimits().pipe(
          map((limits) => getOtherSideTransactionLimitsSuccessAction({ limits })),
          catchError(({ errors }) => {
            this.messageService.showErrors(errors, 'Could not get payout limits');
            return of(getOtherSideTransactionLimitsErrorAction({ errors }));
          }),
        ),
      ),
    ),
  );

  updatePayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePayoutAction),
      switchMap(({ hashId, payoutPatch }) =>
        this.otherSideTransactionService.updatePayoutDraft(hashId, payoutPatch).pipe(
          map((payout) => updatePayoutSuccessAction({ payout })),
          catchError((errors) => of(updatePayoutErrorAction(errors))),
        ),
      ),
    ),
  );

  showSuccessMessageAndGoToDetailOnPayoutUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updatePayoutSuccessAction),
        tap(({ payout: { id } }) => {
          this.messageService.success(`Payout updated!`);
          this.router.navigateByUrl(`/platform/payouts/detail/${id}`);
        }),
      ),
    { dispatch: false },
  );

  showErrorMessageOnPayoutUpdate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updatePayoutErrorAction),
        tap(({ errors }) => {
          this.messageService.showErrors(errors, `Failed to update payout`);
        }),
      ),
    { dispatch: false },
  );
}
